import { call, fork, select } from 'redux-saga/effects';
import { selectE2eeEnabled } from 'features/e2ee/e2eeSlice';
import { signalingWhiteboardKeyRequested } from 'features/e2ee/actions';
import { selectRoomId } from 'features/room/roomSlice';
import { exchangeWhiteboardKeySaga } from 'features/e2ee/sagas/exchangeWhiteboardKeySaga';
import type { User } from 'features/users/types';
import { selectUserById } from 'features/users/usersSlice';
import { AesCm256EncryptionKey } from 'features/e2ee/AesCm256EncryptionKey';
import { E2EEManager } from 'features/e2ee/E2EEManager';
import type { Recorder } from 'features/recorder/types';
import { selectRecorder } from 'features/recorder/recorderSlice';
import type { Restreamer } from 'features/restreamer/types';
import { selectRestreamer } from 'features/restreamer/restreamerSlice';

export function* onWhiteboardKeyRequestedSaga(
  action: ReturnType<typeof signalingWhiteboardKeyRequested>
) {
  const e2eeEnabled: boolean = yield select(selectE2eeEnabled);
  if (e2eeEnabled) {
    const roomId: string = yield select(selectRoomId);
    const { userId, id } = action.payload;

    const whiteboardKey: AesCm256EncryptionKey | undefined = yield call(
      E2EEManager.whiteboard.getKey,
      id,
      roomId
    );

    // ignore if we don't have requested key
    if (!whiteboardKey) {
      return;
    }

    const user: User = yield select(selectUserById, userId);

    const recorder: Recorder | undefined = yield select(selectRecorder);
    const isRecorder = recorder?.id === userId;

    const restreamer: Restreamer | undefined = yield select(selectRestreamer);
    const isRestreamer = restreamer?.id === userId;

    // don't send the key to left user
    if (!user && !isRecorder && !isRestreamer) {
      return;
    }

    // Determine the target based on which entity matches the userId
    let targetUser: User | Recorder | Restreamer;
    if (isRecorder) {
      targetUser = recorder!;
    } else if (isRestreamer) {
      targetUser = restreamer!;
    } else {
      targetUser = user;
    }

    yield fork(exchangeWhiteboardKeySaga, id, {
      id: userId,
      roomId,
      e2eePublicKey: targetUser.e2eePublicKey!,
    });
  }
}
